import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { ISummaryField } from './SummaryField.interface';
import { Button } from '@zurich-primitives/button';
import { Toolbar } from 'app/components';
import {
  useAppDispatch,
  useAppSelector,
  getCurrentSummary,
  setCurrentSummary,
  setSummaryEdited,
  setSelectedHighlight,
  getService,
  getShowHighlights
} from 'app/redux';
import { ReactComponent as UndoIcon } from 'app/assets/Icons/Return.svg';
import styles from './SummaryField.module.scss';
import { IData, IPutData } from 'app/utils';
import classnames from 'classnames';
import { SERVICE_NAMES } from 'app/utils/consts';

export const SummaryField: React.FC<
  ISummaryField & HTMLAttributes<HTMLDivElement>
> = ({
  title,
  value,
  currentTitle,
  setCurrentTitle,
  selectedType,
  fieldStyle = undefined,
  showToolbar = true,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const currentSummary = useAppSelector(getCurrentSummary);
  const [editEnabled, setEditEnabled] = useState<boolean>(false);
  const [deletedItem, setDeletedItem] = useState<boolean>(false);
  const [fieldValue, setFieldValue] = useState<string>();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const currentService = useAppSelector(getService);
  const showHighlights = useAppSelector(getShowHighlights);

  const handleOnChange = (value: string): void => {
    if (currentSummary != null) {
      const currentHash = currentSummary.hash as string;
      const currentType = currentSummary.type as string;
      const newArray = [...(currentSummary as IPutData).data];
      (currentSummary.data as IData[]).forEach((field: IData) => {
        if (field.key === title) {
          newArray[(currentSummary.data as IData[]).indexOf(field)] = {
            key: title,
            value,
            updated: true
          };
        }
      });
      const updatedSummary: IPutData = {
        hash: currentHash,
        type: currentType,
        data: newArray
      };

      currentHash && dispatch(setCurrentSummary(updatedSummary));
      dispatch(setSummaryEdited(true));
    }
  };

  const handleEdit = (): void => {
    const field = textareaRef?.current;
    setEditEnabled((prev) => !prev);
    if (!editEnabled) {
      if (field != null) {
        field.focus();
        field.style.border = '1px solid var(--color-grayscale-gray-3)';
      }
    } else {
      if (field != null) {
        field.blur();
        field.style.border = '1px solid var(--color-grayscale-gray-4)';
      }
    }
  };

  const handleDelete = (): void => {
    setDeletedItem(true);
    const field = textareaRef?.current;
    if (field != null) {
      if (currentSummary != null) {
        setFieldValue(field.value);
        const newArray = [...(currentSummary as IPutData).data];
        (currentSummary.data as IData[]).forEach((field: any) => {
          if (field.key === title) {
            newArray.splice((currentSummary.data as IData[]).indexOf(field), 1);
          }
        });
        const currentHash = currentSummary.hash as string;
        const currentType = currentSummary.type as string;

        currentHash &&
          dispatch(
            setCurrentSummary({
              hash: currentHash,
              type: currentType,
              data: newArray
            })
          );
        dispatch(setSummaryEdited(true));
      }
    }
  };

  const handleUndo = (): void => {
    setDeletedItem(false);
    dispatch(setSummaryEdited(true));

    const newArray = [...(currentSummary as IPutData).data];
    fieldValue &&
      newArray.push({
        key: title,
        value: fieldValue,
        updated: false
      });
    const currentHash = currentSummary?.hash as string;
    const currentType = currentSummary?.type as string;

    currentHash &&
      dispatch(
        setCurrentSummary({
          hash: currentHash,
          type: currentType,
          data: newArray
        })
      );
  };

  const handleSelectedHighlight = (title: string): void => {
    if (!setCurrentTitle) return;
    setCurrentTitle(title);
    const fields = currentSummary?.data as IData[];
    let hihglightKey;

    const foundField = fields.find((field, index) => {
      if (field.key === title) {
        hihglightKey = index + 1;

        return true;
      }

      return false;
    });

    if (foundField && hihglightKey && showHighlights) {
      dispatch(setSelectedHighlight(hihglightKey));
    }
  };

  const autoResize = (element: HTMLTextAreaElement): void => {
    element.style.height = '';

    const numberOfLines = element.value.split('\n').length;
    const valueLenght = element.value.length;

    if (numberOfLines > 1 || valueLenght > 90) {
      element.style.height = `${element.scrollHeight - 20}px`;
    } else {
      element.style.height = '24px';
    }
  };

  useEffect(() => {
    if (textareaRef?.current) autoResize(textareaRef.current);
  }, [value]);

  useEffect(() => {
    const field = document.getElementById(
      `textarea-${title}`
    ) as HTMLTextAreaElement;

    if (!deletedItem && fieldValue !== undefined)
      if (field != null) {
        field.value = fieldValue;
        field.style.height = `${field.scrollHeight}px`;
      }
  }, [deletedItem]);

  useEffect(() => {
    const titleElements = document.querySelectorAll(
      "button[class*='SummaryField_insightsTitle_']"
    );
    [].forEach.call(titleElements, (titleElement: HTMLButtonElement) => {
      if (titleElement.innerText === currentTitle) {
        titleElement.classList.add(styles.selectedTitle);
      } else {
        titleElement.classList.remove(styles.selectedTitle);
      }
    });
  }, [currentTitle]);

  return (
    <div className={styles.field} {...rest}>
      {(currentService === SERVICE_NAMES.CONTRACT_INSIGHTS ||
        currentService === SERVICE_NAMES.CLAIMS_INSIGHTS) &&
      showHighlights ? (
        <button
          onClick={() => handleSelectedHighlight(title)}
          className={classnames(styles.insightsTitle)}
        >
          {title}
        </button>
      ) : (
        <h4 className={styles.title}>{title}</h4>
      )}

      <div>
        {deletedItem ? (
          <div className={styles.undo}>
            Field deleted{' '}
            <Button onClick={handleUndo} variant="tertiary">
              <Button.Icon>
                <UndoIcon />
              </Button.Icon>
              <Button.Label>Undo</Button.Label>
            </Button>
          </div>
        ) : (
          <>
            <textarea
              ref={textareaRef}
              id={`textarea-${title}`}
              disabled={!editEnabled}
              className={styles.textarea}
              style={{ fontFamily: 'monospace', ...fieldStyle }}
              onChange={(e) => handleOnChange(e.target.value)}
              onInput={(e) => {
                const element = e.target as HTMLTextAreaElement;
                autoResize(element);
              }}
              value={value}
            />
            {showToolbar ? (
              <Toolbar
                title={title}
                onEdit={handleEdit}
                onDelete={handleDelete}
                selectedType={selectedType}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
